/* eslint-disable no-restricted-imports */
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { useCart } from '@/data/Content/Cart';
import { useProduct } from '@/data/Content/Product';
import { fetcherESpotDataFromName } from '@/data/Content/_ESpotDataFromName';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { usePageDataFromId } from '@/data/_PageDataFromId';
import { useSettings } from '@/data/Settings';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { getLastAddedChildPartNumber } from 'integration/utils/getLastAddedChildPartNumber';
import { ID } from '@/data/types/Basic';
import { ProcessedContent } from '@/data/types/Marketing';
import { dataMapContent } from '@/data/utils/dataMapContent';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getESpotParams } from '@/data/utils/getESpotQueryParams';
import valueIsRecord from 'integration/utils/typeAssertions/valueIsRecord';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';
import { useCallback, useMemo } from 'react';
import { isDialogDismissed } from '@/utils/dismissibleDialog';
import { FREE_SHIPPING_POPUP_STORAGE_KEY } from '@/data/Content/FreeShippingActivatedPopup';

export const EMS_KEY: ID = 'DPSV2PopupRecomendationToAddCart';

const FREE_SHIPPING_POPUP_TAG = 'dp-free-shipping-activated-popup';

const EXCLUDED_BRANDS: string[] = [];

const isExcludedBrand = (brand?: string) =>
	!!brand && EXCLUDED_BRANDS.includes(brand.trim().toLocaleLowerCase());

export interface FreeShippingActivatedPopupRecommendationProps {
	id?: string;
	title: string;
	description: string;
	descriptionSecondItem?: string;
	products: Record<string, string>;
	discount?: number;
}

const getTagAttribute = (tag: string, attribute: string) => {
	const match = tag.match(new RegExp(`\\s${attribute}="([^"]*)"`));

	return match?.at(1);
};

const parseProducts = (products?: string) => {
	if (!products) {
		return undefined;
	}

	try {
		const parsed = JSON.parse(products.replace(/'/g, '"'));
		const entries = valueIsRecord(parsed) ? Object.entries(parsed) : [];

		if (entries.length === 0) {
			return undefined;
		}

		return entries.reduce<Record<string, string> | undefined>((acc, [key, value]) => {
			if (!acc) {
				return undefined;
			}

			if (!valueIsString(value)) {
				return undefined;
			}

			return { ...acc, [key]: value };
		}, {});
	} catch {
		return undefined;
	}
};

const parseDiscount = (discount?: string) => {
	if (!discount) {
		return undefined;
	}

	const value = Number(discount.replace(/[^\d.]/g, ''));

	return isNaN(value) || value <= 0 ? undefined : value;
};

export const getFreeShippingActivatedPopupPropsFromContent = (
	content?: ProcessedContent[]
): FreeShippingActivatedPopupRecommendationProps | undefined => {
	const text = content?.find(({ text }) => text?.includes(`<${FREE_SHIPPING_POPUP_TAG}`))?.text;
	const tag = text?.match(new RegExp(`<${FREE_SHIPPING_POPUP_TAG}[^>]*>`))?.at(0);

	if (!tag) {
		return undefined;
	}

	const id = getTagAttribute(tag, 'id');
	const title = getTagAttribute(tag, 'title');
	const description = getTagAttribute(tag, 'description');
	const descriptionSecondItem = getTagAttribute(tag, 'descriptionSecondItem');
	const products = parseProducts(getTagAttribute(tag, 'products'));
	const discount = parseDiscount(getTagAttribute(tag, 'discount'));

	if (!title || !description || !products) {
		return undefined;
	}

	return { id, title, description, descriptionSecondItem, products, discount };
};

export const useFreeShippingActivatedPopupRecommendation = () => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { breadcrumb } = useBreadcrumbTrail();
	const params = useExtraRequestParameters();
	const { data: pageData } = usePageDataFromId();
	const { orderItems } = useCart();
	const lastAddedPartNumber = useMemo(() => getLastAddedChildPartNumber(orderItems), [orderItems]);
	const { product: lastAddedProduct } = useProduct({ id: lastAddedPartNumber });
	const lastAddedBrand = useMemo(
		() => findProductAttribute(lastAddedProduct?.attributes ?? [], 'Marca')?.value,
		[lastAddedProduct]
	);
	const { storeId, langId, defaultCatalogId: catalogId } = getClientSideCommon(settings, router);
	const queryBase = useMemo(
		() => ({ catalogId, DM_ReturnCatalogGroupId: true, DM_FilterResults: false, langId }),
		[catalogId, langId]
	);
	const getFreeShippingActivatedPopupProps = useCallback(async () => {
		if (!storeId || isDialogDismissed(FREE_SHIPPING_POPUP_STORAGE_KEY)) {
			return undefined;
		}
		if (isExcludedBrand(lastAddedBrand)) {
			return undefined;
		}

		const props = getESpotParams({
			pageData,
			query: router.query,
			emsName: EMS_KEY,
			queryBase,
			breadcrumb,
		});

		const data = await fetcherESpotDataFromName(true)(storeId, props.emsName, props.query, params);

		return getFreeShippingActivatedPopupPropsFromContent(dataMapContent(data));
	}, [breadcrumb, pageData, params, queryBase, router.query, storeId, lastAddedBrand]);

	return { getFreeShippingActivatedPopupProps };
};
